// CTV-468 — Self-service analytics facade.
//
// One module, two sinks. Components NEVER call posthog/gtag directly — they
// import from here so the two providers stay in sync by construction.
//
//   PostHog → product analytics: funnels, retention, session replay.
//             Receives every event.
//   GA4     → marketing/acquisition + Google-Ads attribution.
//             Receives pageviews + the conversion events in GA4_FORWARD.
//
// Each provider self-disables when its env key is absent, so local dev and
// previews stay silent (no real keys committed — see .env.example). US-only
// product, so there is no consent gate; identify uses the Supabase user id
// only (never email/PII).
import posthog from 'posthog-js';
import { isSelfServiceDevelopment } from './env';

// Canonical event names. The single source of truth — never inline a raw
// string at a call site, or GA4 and PostHog will drift.
export const EVENTS = {
  // Funnel
  STATION_VIEWED: 'station_viewed',
  WIZARD_STARTED: 'wizard_started',
  WIZARD_STEP_VIEWED: 'wizard_step_viewed',
  WIZARD_STEP_COMPLETED: 'wizard_step_completed',
  // Auth
  OTP_REQUESTED: 'otp_requested',
  OTP_VERIFIED: 'otp_verified',
  OTP_FAILED: 'otp_failed',
  // Conversion
  PAYMENT_STARTED: 'payment_started',
  SUBMISSION_COMPLETED: 'submission_completed',
  // Friction (codes/categories only — never raw messages or payloads)
  PAYMENT_FAILED: 'payment_failed',
  CAMPAIGN_NAME_CONFLICT: 'campaign_name_conflict',
  CREATIVE_UPLOAD_FAILED: 'creative_upload_failed',
  // Creative — direct upload
  CREATIVE_UPLOAD_STARTED: 'creative_upload_started',
  CREATIVE_UPLOAD_SUCCEEDED: 'creative_upload_succeeded',
  // Creative — Waymark builder lifecycle
  WAYMARK_GENERATION_STARTED: 'waymark_generation_started',
  WAYMARK_GENERATION_FAILED: 'waymark_generation_failed',
  WAYMARK_TOPIC_FAILED: 'waymark_topic_failed',
  WAYMARK_READY: 'waymark_ready',
  WAYMARK_EDITOR_OPENED: 'waymark_editor_opened',
  WAYMARK_EDITOR_EXITED: 'waymark_editor_exited',
  WAYMARK_VIDEO_COMPLETED: 'waymark_video_completed',
  WAYMARK_VIDEO_RENDERED: 'waymark_video_rendered',
  WAYMARK_COMMITTED: 'waymark_committed',
  WAYMARK_REJECTED_SPECS: 'waymark_rejected_specs',
  // Drafts / resume
  DRAFT_CREATED: 'draft_created',
  DRAFT_RESUMED: 'draft_resumed',
  // Retention
  MY_CAMPAIGNS_VIEWED: 'my_campaigns_viewed',
  CAMPAIGN_DETAIL_VIEWED: 'campaign_detail_viewed',
  DELIVERY_REPORT_VIEWED: 'delivery_report_viewed',
  CAMPAIGN_RERUN_STARTED: 'campaign_rerun_started',
  // Campaign management — post-launch (CTV-440 Edit modal: extend / swap /
  // cancel, plus report/receipt downloads and failed-payment recovery)
  EDIT_CAMPAIGN_OPENED: 'edit_campaign_opened',
  CAMPAIGN_EXTEND_STARTED: 'campaign_extend_started',
  CAMPAIGN_EXTENDED: 'campaign_extended',
  CAMPAIGN_EXTEND_FAILED: 'campaign_extend_failed',
  CAMPAIGN_CANCELED: 'campaign_canceled',
  // CTV-466 replaced the single-file swap flow with a multi-creative
  // "edit commercial set" editor (EditCreativesTabPanel → /update-creative-set).
  // One event on a successful save; the old swap_started/swapped/voided are gone.
  CREATIVE_SET_UPDATED: 'creative_set_updated',
  DELIVERY_REPORT_DOWNLOADED: 'delivery_report_downloaded',
  RECEIPT_DOWNLOADED: 'receipt_downloaded',
  PAYMENT_RECOVERY_STARTED: 'payment_recovery_started',
  PAYMENT_RECOVERED: 'payment_recovered',
  // Account / team management (advertiser-side)
  TEAM_MEMBER_INVITED: 'team_member_invited',
  TEAM_MEMBER_REVOKED: 'team_member_revoked',
  ADVERTISER_PROFILE_UPDATED: 'advertiser_profile_updated',
  INVITATION_ACCEPTED: 'invitation_accepted',
  CAMPAIGN_ATTENTION_ACKED: 'campaign_attention_acked',
  // Publisher portal — submission review & ops (a different audience; every
  // event carries the `role` super-property set in useAuth)
  PAYMENT_CAPTURED: 'payment_captured',
  CREATIVE_APPROVED: 'creative_approved',
  CREATIVE_REJECTED: 'creative_rejected',
  SUBMISSION_REJECTED: 'submission_rejected',
  CREATIVE_SWAP_APPROVED: 'creative_swap_approved',
  CREATIVE_SWAP_REJECTED: 'creative_swap_rejected',
  PUSH_RETRIED: 'push_retried',
  PUBLICA_PUSH_RETRIED: 'publica_push_retried',
} as const;

// Events mirrored to GA4 (conversions). Everything reaches PostHog regardless;
// only these are forwarded to GA4 so marketing can attribute them to a source.
const GA4_FORWARD = new Set<string>([EVENTS.OTP_VERIFIED, EVENTS.SUBMISSION_COMPLETED]);

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let posthogReady = false;
let ga4Ready = false;
// Guard so AuthProvider re-mounts (the router wraps each route in its own
// provider) don't re-fire identify on every navigation.
let lastIdentified: string | null = null;

function initGa4(measurementId: string): void {
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  // gtag.js consumes the native `arguments` object pushed verbatim. Pushing a
  // plain array instead makes GTM treat the entry as data — not a js/config/
  // event command — so it silently never sends a single beacon. Keep Google's
  // canonical classic-function shape; do NOT refactor this to push an array.
  const gtag: (...args: unknown[]) => void = function () {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag = gtag;
  gtag('js', new Date());
  // Manual pageviews — this is a SPA, so gtag's auto page_view would only fire
  // on the initial hard load. page() handles client-side navigation.
  gtag('config', measurementId, { send_page_view: false });
  ga4Ready = true;
}

// Initialize both providers. Reads env lazily (not at module load) so tests
// can stub import.meta.env per-case. Safe to call once at app boot.
export function init(): void {
  const posthogKey = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  const posthogHost =
    (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? 'https://us.i.posthog.com';
  const ga4Id = import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined;

  if (posthogKey && !posthogReady) {
    posthog.init(posthogKey, {
      api_host: posthogHost,
      // Explicit events only — no autocapture noise; the funnel is hand-built.
      autocapture: false,
      capture_pageview: false,
      // Capture $pageleave (tab-close / navigate-away) so funnel drop-off and
      // time-on-step are attributable. Default is 'if_capture_pageview', which
      // would disable it since we send manual pageviews — so set it explicitly.
      capture_pageleave: true,
      // Don't mint a person profile for every anonymous visitor; funnel events
      // are still captured anonymously and stitched on identify().
      person_profiles: 'identified_only',
      // Session replay on, but never record what users type. Stripe card fields
      // live in a cross-origin iframe and are never in our DOM regardless.
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '[data-ph-mask]',
      },
    });
    posthogReady = true;
  }

  if (ga4Id && !ga4Ready) {
    initGa4(ga4Id);
  }

  // Tag every event with the environment. Separate PostHog projects (keyed by
  // a per-environment VITE_POSTHOG_KEY) are the real dev/prod boundary; this is
  // belt-and-suspenders so dev/prod is filterable within a project and obvious
  // on raw events.
  if (posthogReady || ga4Ready) {
    registerContext({ environment: isSelfServiceDevelopment() ? 'dev' : 'prod' });
  }
}

// Cross-cutting context attached to EVERY subsequent event (PostHog
// super-properties + GA4 default params). Call when station/auth context
// becomes known or changes — e.g. { station_slug } and { auth_state }.
export function registerContext(props: Record<string, unknown>): void {
  if (posthogReady) posthog.register(props);
  if (ga4Ready && window.gtag) window.gtag('set', props);
}

export function page(path: string): void {
  if (posthogReady) posthog.capture('$pageview', { path });
  if (ga4Ready && window.gtag) window.gtag('event', 'page_view', { page_path: path });
}

export function track(event: string, props?: Record<string, unknown>): void {
  if (posthogReady) posthog.capture(event, props);
  if (ga4Ready && window.gtag && GA4_FORWARD.has(event)) {
    window.gtag('event', event, props ?? {});
  }
}

// User-id only — no email/PII. Idempotent across provider re-mounts.
export function identify(userId: string): void {
  if (!userId || userId === lastIdentified) return;
  lastIdentified = userId;
  if (posthogReady) posthog.identify(userId);
  if (ga4Ready && window.gtag) window.gtag('set', { user_id: userId });
}

export function reset(): void {
  if (lastIdentified === null) return;
  lastIdentified = null;
  if (posthogReady) posthog.reset();
  if (ga4Ready && window.gtag) window.gtag('set', { user_id: undefined });
}
